// apps/support-console/src/store.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AgentSession {
  token: string;
  userId: string;
  email: string;
  displayName: string;
  role: 'SUPPORT_AGENT' | 'ADMIN';
}

interface AuthState {
  session: AgentSession | null;
  setSession: (s: AgentSession | null) => void;
  logout: () => void;
}

/**
 * Persisted JWT session. The token is the SAME one the main API mints,
 * carrying `role: 'SUPPORT_AGENT' | 'ADMIN'`.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      session: null,
      setSession: (session) => set({ session }),
      logout: () => set({ session: null }),
    }),
    { name: 'ayiland_support_console_session' },
  ),
);

interface UIState {
  theme: 'light' | 'dark';
  setTheme: (t: 'light' | 'dark') => void;
  toggleTheme: () => void;
  language: 'fr' | 'en' | 'fon';
  setLanguage: (l: 'fr' | 'en' | 'fon') => void;
}

function applyTheme(t: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', t === 'dark');
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleTheme: () => {
        const next = get().theme === 'light' ? 'dark' : 'light';
        applyTheme(next);
        set({ theme: next });
      },
      language: 'fr',
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'ayiland_support_console_ui',
      onRehydrateStorage: () => (s) => {
        if (s?.theme) applyTheme(s.theme);
      },
    },
  ),
);
